import React, { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import cn from "classnames";

import { CSVUploadResult } from "@ascendnow/shared";

import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscButton from "../AscButton/AscButton";
import AscCSVTable from "./AscCSVTable/AscCSVTable";
import AscUploadLoader from "./AscUploadLoader/AscUploadLoader";
import AscUploadArea from "./AscUploadArea/AscUploadArea";
import AscParagraph from "../AscTypography/AscParagraph/AscParagraph";
import AscTooltip from "../AscTooltip/AscTooltip";
import AscModal from "../AscModal/AscModal";
import {
  AdminTooltipIcon,
  AdminSuccessIcon,
  RetakeIcon,
  CloseIcon,
} from "../AscIcons";

import styles from "./AscAdminCSVUploader.module.scss";

export interface AscAdminCSVUploaderProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  resetCsvResults: () => void;
  onSCVFileUpload: (file: File, onSuccess: () => void) => Promise<void>;
  csvUploadResult: CSVUploadResult | null;
  sampleLink: string;
}

export default function AscAdminCSVUploader({
  visible,
  setVisible,
  onSCVFileUpload,
  csvUploadResult,
  resetCsvResults,
  sampleLink = "https://docs.google.com/spreadsheets/d/1R1PrcjqN7oYBFx_qlf29tWc1B2hd8hNlIQ-wKOME9_M/edit?usp=sharing",
}: AscAdminCSVUploaderProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);

  const handleCSVUpload = React.useCallback((acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);

    acceptedFiles.forEach((file: Blob) => {
      setLoading(true);

      const reader = new FileReader();

      reader.onabort = () => {
        console.log("file reading was aborted");
        setLoading(false);
      };
      reader.onerror = () => {
        console.log("file reading has failed");
        setLoading(false);
      };
      reader.onload = () => {
        const csvData = Papa.parse(`${reader.result}`, {
          header: true,
        });
        setData(csvData.data);
        setLoading(false);
      };

      reader.readAsText(file);
    });
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      "text/csv": [".csv"],
    },
    onDrop: handleCSVUpload,
  });

  function onContinueUpload() {
    if (file) {
      onSCVFileUpload(file, () => {
        setIsSuccess(true);
        setVisible(true);
      });
    }
  }

  const onClose = useCallback(() => {
    setVisible(false);
    setFile(null);
    setData(null);
    resetCsvResults();
  }, [setVisible, resetCsvResults]);

  //Holds if we managed to upload all csv students successfully
  //Based on this updating UI:
  const isCSVUploadSuccessAll = useMemo(
    () =>
      csvUploadResult &&
      csvUploadResult.totalCount - csvUploadResult.succesfullImports === 0,
    [csvUploadResult]
  );

  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      onClose={onClose}
      modalClassName={cn(
        styles.modal,
        csvUploadResult && styles.result,
        !isCSVUploadSuccessAll && styles.error
      )}
      withBackground={false}
    >
      <div className={styles.content}>
        {!csvUploadResult && (
          <>
            <span className={styles.title}>Upload CSV File</span>
            {!data && (
              <AscTooltip
                tooltipAnchor={
                  <div className={styles.tooltipAnchor}>
                    <div className={styles.tooltipButton}>
                      <AdminTooltipIcon />
                      <span className={styles.tooltipTitle}>
                        View instruction
                      </span>
                    </div>
                    <a
                      href={sampleLink}
                      className={styles.sampleLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Sample
                    </a>
                  </div>
                }
                className={styles.tooltip}
                tooltipContentClassName={styles.tooltipContentClassName}
              >
                <div className={styles.tooltipContent}>
                  <div className={styles.steps}>
                    <span className={styles.number}>1</span>
                    <AscParagraph className={styles.text}>
                      Enter, for example, in Sheets <br />
                      <b className={styles.bold}>
                        Name, Email, Batch and Parent Email - required <br />
                        Birth Date, Gender, County and City - optional
                      </b>
                    </AscParagraph>
                  </div>

                  <div className={styles.steps}>
                    <span className={styles.number}>2</span>
                    <AscParagraph className={styles.text}>
                      To quickly load data from Sheets press{" "}
                      <b className={styles.bold}>
                        File &#62; Download &#62; Comma-separated values (.CSV)
                      </b>{" "}
                      and than add file here
                    </AscParagraph>
                  </div>
                </div>
              </AscTooltip>
            )}

            {!data && (
              <AscUploadArea
                getRootProps={getRootProps}
                getInputProps={getInputProps}
              />
            )}

            {!!data && !loading && (
              <React.Fragment>
                <div
                  className={styles.introduction}
                  onClick={() => {
                    open();
                    setData(null);
                    setFile(null);
                  }}
                >
                  <div className={styles.tooltipTitleWrapper}>
                    <RetakeIcon />
                    <span className={styles.tooltipTitle}>Retake file</span>
                  </div>
                </div>
                <AscCSVTable data={data} />
              </React.Fragment>
            )}

            {!!loading && <AscUploadLoader percentage={100} />}

            {!loading && (
              <div className={styles.buttonContainer}>
                <AscButton
                  className={styles.button}
                  variant="secondary"
                  onClick={onClose}
                >
                  Cancel
                </AscButton>
                {data ? (
                  <AscButton
                    className={styles.button}
                    onClick={onContinueUpload}
                    disabled={data?.length < 1}
                  >
                    Continue
                  </AscButton>
                ) : (
                  <AscButton className={styles.button} onClick={open}>
                    <input {...getInputProps()} />
                    Upload
                  </AscButton>
                )}
              </div>
            )}
          </>
        )}
        {csvUploadResult && (
          <div className={styles.success}>
            {isCSVUploadSuccessAll ? (
              <AdminSuccessIcon />
            ) : (
              <span className={styles.error}>
                <CloseIcon />
              </span>
            )}
            <AscHeading level={2} className={styles.successTitle}>
              {isCSVUploadSuccessAll ? "Success" : "Failed"}
            </AscHeading>
            <AscParagraph>
              Information about Students <br />{" "}
              {isCSVUploadSuccessAll
                ? `uploaded successfully ${csvUploadResult.succesfullImports}/${csvUploadResult.totalCount}`
                : `failed to upload ${
                    csvUploadResult &&
                    csvUploadResult?.totalCount -
                      csvUploadResult?.succesfullImports
                  }/${csvUploadResult?.totalCount}`}
            </AscParagraph>
            {!isCSVUploadSuccessAll && (
              <div className={styles.failedList}>
                {csvUploadResult?.failedStudentArray &&
                  Array.isArray(csvUploadResult?.failedStudentArray) &&
                  csvUploadResult?.failedStudentArray?.map((el) => (
                    <AscParagraph key={`${el.name}-${el.email}`}>
                      {el.email}
                      {el?.importError ? " - " + el?.importError : ""}
                    </AscParagraph>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AscModal>
  );
}
